import cn from "classnames";
import { type ComponentPropsWithoutRef, type PropsWithChildren } from "react";
import Anchor, { type AnchorProps } from "@/components/common/Anchor";
import { ButtonColor, ButtonSize, ButtonVariant } from "@/constants/button";
import s from "./Button.module.scss";
import { useButtonEndIcon, useButtonStartIcon } from "./hooks";
import { type Icon } from "./utils";

type HTMLButton = Omit<ComponentPropsWithoutRef<"button">, "color">;

interface BaseButton {
  size?: ButtonSize;
  color?: ButtonColor;
  variant?: ButtonVariant;
  StartIcon?: Icon;
  EndIcon?: Icon;
}

export type RegularButton = HTMLButton & BaseButton;

export type LinkButton = Pick<HTMLButton, "disabled" | "className"> &
  AnchorProps &
  BaseButton;

export type ButtonProps = RegularButton | LinkButton;

export default function Button(props: PropsWithChildren<ButtonProps>) {
  const {
    size = ButtonSize.Medium,
    color = ButtonColor.Primary,
    variant = ButtonVariant.Contained,
    disabled,
    StartIcon,
    EndIcon,
    children,
    ...buttonProps
  } = props;

  const CoreStartIcon = useButtonStartIcon(color, StartIcon);
  const CoreEndIcon = useButtonEndIcon(color, EndIcon);

  const className = cn(s.button, buttonProps.className, "informa-button", {
    [s.small]: size === ButtonSize.Small,
    [s.medium]: size === ButtonSize.Medium,
    [s.large]: size === ButtonSize.Large,
    [s.primary]: color === ButtonColor.Primary,
    [s.secondary]: color === ButtonColor.Secondary,
    [s.tertiary]: color === ButtonColor.Tertiary,
    [s.quaternary]: color === ButtonColor.Quaternary,
    [s.contained]: variant === ButtonVariant.Contained,
    [s.outlined]: variant === ButtonVariant.Outlined,
    [s.text]: variant === ButtonVariant.Text,
    [s.withIcon]: CoreStartIcon || CoreEndIcon,
    [s.disabled]: disabled,
  });

  const label = (
    <>
      {CoreStartIcon && (
        <span className={cn(s.startIcon, "informa-button-start-icon")}>
          <CoreStartIcon />
        </span>
      )}
      {children}
      {CoreEndIcon && (
        <span className={cn(s.endIcon, "informa-button-end-icon")}>
          <CoreEndIcon />
        </span>
      )}
    </>
  );

  if ("href" in buttonProps) {
    return (
      <Anchor {...buttonProps} className={className}>
        {label}
      </Anchor>
    );
  }

  return (
    <button {...buttonProps} disabled={disabled} className={className}>
      {label}
    </button>
  );
}
